import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiEmail, ApiEmailSequenceRunStatus } from "@/types/integrations/integration.types.ts";
import { Box, ListItem, Stack, Tag, Text, Title } from "@skailer-com/uikit";
import { isSameDay } from "date-fns";
import { useTranslation } from "react-i18next";

export interface EmailSequenceRunDetailsProps {
  message: ApiEmail & { sequence_run: NonNullable<ApiEmail["sequence_run"]> };
}

const statusVariants: Record<
  ApiEmailSequenceRunStatus,
  "primary" | "positive" | "warning" | "danger"
> = {
  [ApiEmailSequenceRunStatus.active]: "primary",
  [ApiEmailSequenceRunStatus.completed]: "positive",
  [ApiEmailSequenceRunStatus.replied]: "positive",
  [ApiEmailSequenceRunStatus.canceled]: "warning",
  [ApiEmailSequenceRunStatus.failed]: "danger",
};

export function EmailSequenceRunDetails({ message }: EmailSequenceRunDetailsProps) {
  const { t } = useTranslation();
  const { renderDate, renderDateAndTime, renderTime } = useFormatter();
  const run = message.sequence_run;
  const currentEmail = message.sequence_position ?? 1;
  const totalEmails = message.sequence_total ?? 1;
  const isLatestEmail = currentEmail === run.sent_follow_ups + 1;
  const isActive = run.status === ApiEmailSequenceRunStatus.active;
  const nextSendAtFrom = run.datetime_next_send_at_from
    ? renderDateAndTime(new Date(run.datetime_next_send_at_from))
    : null;
  const nextSendAtTo = run.datetime_next_send_at_to
    ? renderDateAndTime(new Date(run.datetime_next_send_at_to))
    : null;
  const nextSendInterval = (() => {
    if (!run.datetime_next_send_at_from || !run.datetime_next_send_at_to) return null;

    const dateFrom = new Date(run.datetime_next_send_at_from);
    const dateTo = new Date(run.datetime_next_send_at_to);

    if (isSameDay(dateFrom, dateTo)) {
      return t("ats:email_sequence_next_send_interval_same_day", {
        date: renderDate(dateFrom, "d MMMM"),
        time_from: renderTime(dateFrom),
        time_to: renderTime(dateTo),
      });
    }

    return t("ats:email_sequence_next_send_interval", {
      date_from: renderDateAndTime(dateFrom),
      date_to: renderDateAndTime(dateTo),
    });
  })();
  const stoppedAt = run.datetime_stopped_at
    ? renderDateAndTime(new Date(run.datetime_stopped_at))
    : null;

  return (
    <Box variant={"border"}>
      <Title header={t("ats:email_sequence_details_title")} paddingBottom size={"xs"} />
      <ListItem
        behavior={"card"}
        title={t("ats:email_sequence_email_position", {
          current: currentEmail,
          total: totalEmails,
        })}
        captionSlot={
          <Stack gap={"xs"}>
            {isLatestEmail && isActive && (nextSendAtFrom || nextSendAtTo) && (
              <Text className={"text-sm text-secondary"}>
                {nextSendInterval
                  ? nextSendInterval
                  : t("ats:email_sequence_next_send", {
                      date: nextSendAtFrom ?? nextSendAtTo,
                    })}
              </Text>
            )}
            {!isActive && stoppedAt && (
              <Text className={"text-sm text-secondary"}>
                {t("ats:email_sequence_stopped", {
                  reason: t(`ats:email_sequence_status_${run.status}`),
                  date: stoppedAt,
                })}
              </Text>
            )}
          </Stack>
        }
        valueSlot={
          <Stack className={"pl-2"}>
            <Tag variant={statusVariants[run.status]}>
              {t(`ats:email_sequence_status_${run.status}`)}
            </Tag>
          </Stack>
        }
      />
    </Box>
  );
}
